/**
 * Sawyer 06 beds-lockers-runcard (B4) — this stay's locker, per dog.
 *
 *   GET  /api/admin/bookings/:bookingId/lockers?date=YYYY-MM-DD
 *   PUT  /api/admin/bookings/:bookingId/lockers[?date=…]  { scope: 'dog', dogId, locker } | { scope: 'booking', locker }
 *
 * Throws like `fetchRunCardEdits` / `saveRunCardEdits` (an `ApiError` whose
 * message is render-safe copy: the server's own sentence for a 400 / 404),
 * so the run-card editor catches both the same way. An older server has no
 * route: the GET throws a 404-status `ApiError` and the editor's locker
 * section says it could not load, while the rest of the editor works.
 */
import { ApiError, parseErrorBody } from './api.js';

export type StayLockerSource = 'stay' | 'bed_default' | 'none';

export interface StayLockersView {
  booking_id: string;
  /** YYYY-MM-DD, the night the bed is read for. */
  night: string;
  locker_follows_bed: boolean;
  bed: { unit_id: string; name: string; size_class: string | null; default_locker: string | null } | null;
  dogs: Array<{
    dog_id: string;
    dog_name: string;
    /** What the card prints (null = the blank box). */
    locker: string | null;
    locker_source: StayLockerSource;
    /** This stay's own value; null = following the bed (or none). */
    stay_locker: string | null;
    set_at: string | null;
  }>;
}

export type StayLockerInput =
  | { scope: 'dog'; dogId: string; locker: string | null }
  | { scope: 'booking'; locker: string | null };

function lockersUrl(apiUrl: string, bookingId: string, date?: string): string {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  return `${apiUrl}/api/admin/bookings/${encodeURIComponent(bookingId)}/lockers${q}`;
}

async function throwFrom(res: Response): Promise<never> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const parsed = parseErrorBody(res.status, body, { fallback: `Request failed (${res.status})` });
  throw new ApiError(res.status, parsed.message, parsed.code);
}

export async function fetchStayLockers(
  apiUrl: string,
  token: string,
  bookingId: string,
  date?: string,
): Promise<StayLockersView> {
  const res = await fetch(lockersUrl(apiUrl, bookingId, date), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return throwFrom(res);
  return (await res.json()) as StayLockersView;
}

export async function saveStayLocker(
  apiUrl: string,
  token: string,
  bookingId: string,
  input: StayLockerInput,
  date?: string,
): Promise<StayLockersView> {
  const res = await fetch(lockersUrl(apiUrl, bookingId, date), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(input),
  });
  if (!res.ok) return throwFrom(res);
  return (await res.json()) as StayLockersView;
}
